// Move stock bin to bin: scan the from-bin and see what's in it, pick the item (and lot) and how many, add more
// items if you like, scan the to-bin, and it's posted in Business Central.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { wms, type Content, type MoveLine, type MoveResult, type RecentMove } from "./wmsApi";
import { Alert, Done, Header, I, LocChip, ScanField, Stepper, Steps, feedback, fmt, errText, errBody } from "./ui";

type Stage = "from" | "items" | "to" | "done";
const keyOf = (r: { itemNo: string; variantCode: string; lotNo: string; serialNo: string }) => [r.itemNo, r.variantCode, r.lotNo, r.serialNo].join("|");

export function MovePage({ loc }: { loc: string }) {
  const [sp] = useSearchParams();
  const [stage, setStage] = useState<Stage>("from");
  const [from, setFrom] = useState<string | null>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const [carry, setCarry] = useState<(MoveLine & { description: string; uom: string })[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<MoveResult | null>(null);
  const [toBin, setToBin] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentMove[]>([]);
  const [hint, setHint] = useState<{ bin: string; qty: number }[]>([]);
  const [undone, setUndone] = useState(false);

  useEffect(() => { wms.recent().then(setRecent).catch(() => {}); }, []);
  useEffect(() => { const f = sp.get("from"); if (f) scanFrom(f); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const left = (r: Content) => r.qty - carry.filter((c) => keyOf(c) === keyOf(r)).reduce((a, c) => a + c.qty, 0);
  const row = contents.find((r) => keyOf(r) === sel) ?? null;

  async function scanFrom(code: string) {
    setBusy(true); setErr(null);
    try {
      const r = await wms.bin(loc, code);
      if (r.bin.blockMovement === "Outbound" || r.bin.blockMovement === "All") { feedback.bad(); setErr(`${r.bin.code} is blocked for taking stock out.`); return; }
      if (!r.contents.length) { feedback.bad(); setErr(`${r.bin.code} is empty. Scan the bin you're taking from.`); return; }
      feedback.ok(); setFrom(r.bin.code); setContents(r.contents); setCarry([]);
      setSel(r.contents.length === 1 ? keyOf(r.contents[0]) : null); setQty(r.contents.length === 1 ? r.contents[0].qty : 1);
      setStage("items");
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }

  /** In a bin: a scan picks the item, or the lot or serial number on it. */
  async function scanItem(code: string) {
    setErr(null);
    const up = code.toUpperCase();
    const byLot = contents.filter((r) => r.lotNo.toUpperCase() === up || r.serialNo.toUpperCase() === up);
    if (byLot.length === 1) { choose(byLot[0]); return; }
    setBusy(true);
    try {
      const r = await wms.scan(loc, code);
      if (r.kind !== "item") { feedback.bad(); setErr(r.kind === "bin" ? `That's bin ${r.bin.code}. Scan an item in ${from}, or go on to the to-bin.` : `${code} isn't an item.`); return; }
      const rows = contents.filter((x) => x.itemNo === r.item.itemNo && (!r.item.variantCode || x.variantCode === r.item.variantCode));
      if (!rows.length) { feedback.bad(); setErr(`${from} has no ${r.item.itemNo}.`); return; }
      if (rows.length === 1) { choose(rows[0]); return; }
      feedback.ok(); setSel(null); setErr(null); setHint([]);
      setLotPrompt(r.item.itemNo);
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  const [lotPrompt, setLotPrompt] = useState<string | null>(null);
  function choose(r: Content) {
    feedback.ok(); setLotPrompt(null); setSel(keyOf(r));
    setQty(r.serialNo ? 1 : Math.min(left(r), sel === keyOf(r) ? qty : left(r)));
  }

  function addSelected(): boolean {
    if (!row) return carry.length > 0;
    if (!(qty > 0)) { setErr("How many are you moving?"); return false; }
    if (qty > left(row) + 1e-9) { setErr(`Only ${fmt(left(row))} ${row.uom} left in ${from}.`); return false; }
    setCarry((c) => [...c, { itemNo: row.itemNo, variantCode: row.variantCode, lotNo: row.lotNo, serialNo: row.serialNo, qty, description: row.description, uom: row.uom }]);
    setSel(null); setQty(1); return true;
  }
  const toNext = () => {
    if (!addSelected()) { if (!row && !carry.length) setErr("Scan or tap the item you're moving."); return; }
    setErr(null); setStage("to");
  };
  useEffect(() => {
    if (stage !== "to" || !carry[0]) return;
    wms.scan(loc, carry[0].itemNo).then((r) => {
      if (r.kind !== "item") return;
      const byBin = new Map<string, number>();
      for (const b of r.bins) if (b.binCode !== from) byBin.set(b.binCode, (byBin.get(b.binCode) ?? 0) + b.qty);
      setHint([...byBin].map(([bin, q]) => ({ bin, qty: q })).sort((a, b) => b.qty - a.qty).slice(0, 2));
    }).catch(() => setHint([]));
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  async function scanTo(code: string) {
    if (!from) return;
    setBusy(true); setErr(null);
    try {
      const r = await wms.move({ location: loc, fromBin: from, toBin: code.toUpperCase(), lines: carry });
      const failed = r.moves.filter((m) => m.status === "failed");
      if (failed.length === r.moves.length) { feedback.bad(); setErr(failed[0].error ?? "Business Central didn't accept the move."); return; }
      failed.length ? feedback.bad() : feedback.done();
      setResult(r); setToBin(code.toUpperCase()); setContents(r.from.contents); setUndone(false); setStage("done");
    } catch (e) {
      feedback.bad();
      const b = errBody(e);
      setErr(b.code === "same_bin" ? `That's ${from}, where it's coming from. Scan the bin you're putting it in.` : errText(e));
    } finally { setBusy(false); }
  }

  async function undo() {
    if (!result) return;
    setBusy(true); setErr(null);
    try {
      for (const m of result.moves.filter((x) => x.status === "posted")) await wms.undo(m.id);
      feedback.ok(); setUndone(true);
      if (from) setContents((await wms.bin(loc, from)).contents);
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  const reset = () => { setStage("from"); setFrom(null); setContents([]); setCarry([]); setSel(null); setResult(null); setErr(null); setHint([]); };
  const moreFromSame = () => { setCarry([]); setSel(null); setResult(null); setErr(null); setStage(contents.length ? "items" : "from"); if (!contents.length) setFrom(null); };

  /** How much of what was moved (the first item, its lot or serial) a bin now holds. */
  const sameAs = (rows: Content[]) => rows.filter((x) => carry[0] && x.itemNo === carry[0].itemNo && x.variantCode === carry[0].variantCode && x.lotNo === carry[0].lotNo && x.serialNo === carry[0].serialNo).reduce((a, x) => a + x.qty, 0);
  const recentBins = useMemo(() => [...new Set(recent.filter((m) => m.location === loc).flatMap((m) => [m.fromBin, m.toBin]))].slice(0, 4), [recent, loc]);
  const stepN = stage === "from" ? 0 : stage === "items" ? 1 : 2;
  const sub = stage === "done" ? "Done" : `Step ${stepN + 1} of 3 · ${["From bin", "Item and quantity", "To bin"][stepN]}`;
  const back = stage === "items" ? () => { setStage("from"); setErr(null); } : stage === "to" ? () => { setStage("items"); setErr(null); } : "/wms";

  return (
    <>
      <Header title="Move stock" sub={sub} back={back} right={<LocChip code={loc} />} />
      {stage !== "done" && <Steps n={3} at={stepN} />}

      {stage === "from" && (
        <>
          <div className="w-prompt">
            <span className="w-prompt-i"><I n="scan" s={60} w={1.6} /></span>
            <h1>Scan the bin you're taking from</h1>
            <p>You'll see what's in it before you pick an item.</p>
          </div>
          <div className="w-foot">
            {err && <Alert title="Try another bin">{err}</Alert>}
            <ScanField label="From bin" placeholder="Scan or type bin code" onScan={scanFrom} busy={busy} />
            {recentBins.length > 0 && (
              <div className="w-field"><div className="w-label">Recent</div>
                <div className="w-chips">{recentBins.map((b) => <button key={b} type="button" className="w-chipbtn w-mono" onClick={() => scanFrom(b)}>{b}</button>)}</div></div>
            )}
          </div>
        </>
      )}

      {stage === "items" && from && (
        <>
          <div className="w-body">
            <div className="w-card w-confirm">
              <span className="w-tick"><I n="check" s={18} w={2.5} /></span>
              <span className="w-row-main"><span className="w-small">From bin</span><span className="w-mono w-strong big">{from}</span></span>
              <button type="button" className="w-link" onClick={() => setStage("from")}>Change</button>
            </div>
            {err && <Alert title="Not that one">{err}</Alert>}
            <ScanField label="Scan an item or lot" onScan={scanItem} busy={busy} />
            <div className="w-label-row"><span className="w-label">In this bin · scan or tap {lotPrompt ? `the lot of ${lotPrompt}` : "an item"}</span><span className="w-muted">{contents.length} line{contents.length === 1 ? "" : "s"}</span></div>
            <div className="w-list">
              {contents.map((r) => {
                const k = keyOf(r), l = left(r);
                return (
                  <button key={k} type="button" aria-pressed={sel === k} disabled={l <= 1e-9} className={`w-row-btn ${sel === k ? "sel" : ""} ${lotPrompt && r.itemNo !== lotPrompt ? "dim" : ""}`} onClick={() => choose(r)}>
                    <span className="w-row-main"><span className="w-strong">{r.itemNo}</span><span className="w-muted">{[r.description, r.lotNo && `Lot ${r.lotNo}`, r.serialNo && `SN ${r.serialNo}`].filter(Boolean).join(" · ")}</span></span>
                    <span className={sel === k ? "w-strong" : "w-muted"}>{fmt(l)} {r.uom}</span>
                  </button>
                );
              })}
            </div>
            {row && (
              <div className="w-card w-qtycard">
                <label htmlFor="move-qty" className="w-label">How many are you moving?</label>
                <Stepper id="move-qty" value={qty} onChange={setQty} max={row.serialNo ? 1 : left(row)} />
                <div className="w-label-row"><span className="w-muted">{fmt(Math.max(0, left(row) - qty))} stay in {from}</span>
                  {!row.serialNo && <button type="button" className="w-chipbtn" onClick={() => setQty(left(row))}>All {fmt(left(row))}</button>}</div>
              </div>
            )}
            {carry.length > 0 && <div className="w-muted">Carrying {carry.length} item{carry.length > 1 ? "s" : ""} already.</div>}
          </div>
          <div className="w-foot">
            {row && <button type="button" className="w-btn" onClick={() => { if (addSelected()) feedback.ok(); }}>Add, and pick another item</button>}
            <button type="button" className="w-btn primary" onClick={toNext} disabled={!row && !carry.length}>Next: scan to-bin <I n="arrow" /></button>
          </div>
        </>
      )}

      {stage === "to" && from && (
        <>
          <div className="w-body">
            <div className="w-label">You're carrying</div>
            <div className="w-card w-rows">
              {carry.map((c, i) => (
                <div key={i} className="w-line">
                  <span className="w-row-main"><span className="w-strong">{c.itemNo} · {fmt(c.qty)} {c.uom}</span><span className="w-muted">{[c.lotNo && `Lot ${c.lotNo}`, c.serialNo && `SN ${c.serialNo}`, <>from <span className="w-mono">{from}</span></>].filter(Boolean).map((x, j) => <span key={j}>{j > 0 && " · "}{x}</span>)}</span></span>
                  <button type="button" className="w-iconbtn" aria-label={`Remove ${c.itemNo}`} onClick={() => { const n = carry.filter((_, j) => j !== i); setCarry(n); if (!n.length) setStage("items"); }}><I n="close" s={20} /></button>
                </div>
              ))}
              <button type="button" className="w-line link w-addline" onClick={() => setStage("items")}><I n="plus" s={20} /> Add another item</button>
            </div>
            <div className="w-prompt small">
              <span className="w-prompt-i"><I n="scan" s={52} w={1.6} /></span>
              <h1>Scan the bin you're putting it in</h1>
              <p>The move posts to Business Central as soon as the bin scans.</p>
            </div>
          </div>
          <div className="w-foot">
            {err && <Alert title="Not moved">{err}</Alert>}
            <ScanField label="To bin" placeholder="Scan or type bin code" onScan={scanTo} busy={busy} />
            {hint.map((h) => (
              <button key={h.bin} type="button" className="w-suggest" onClick={() => scanTo(h.bin)} disabled={busy}>
                <span className="w-row-main"><span className="w-small">Already holds {fmt(h.qty)} of {carry[0]?.itemNo}</span><span className="w-mono w-strong">{h.bin}</span></span>
                <span className="w-link">Use this bin</span>
              </button>
            ))}
          </div>
        </>
      )}

      {stage === "done" && result && from && toBin && (
        <>
          <div className="w-body center">
            {undone ? <Alert tone="ok" title="Undone">Moved back to {from} in Business Central.</Alert> : (
              <>
                <span className="w-bigtick"><I n="check" s={60} w={2.6} /></span>
                <h1 className="w-h1">{result.moves.length === 1 ? `Moved ${fmt(result.moves[0].qty)} ${carry[0]?.uom ?? ""}` : `Moved ${result.moves.filter((m) => m.status === "posted").length} of ${result.moves.length} items`}</h1>
                {result.moves.length === 1 && <p className="w-muted">{[result.moves[0].itemNo, carry[0]?.description, result.moves[0].lotNo && `Lot ${result.moves[0].lotNo}`].filter(Boolean).join(" · ")}</p>}
              </>
            )}
            <div className="w-card w-fromto">
              <span><span className="w-small">From</span><span className="w-mono w-strong big">{from}</span><span className="w-muted">{fmt(sameAs(result.from.contents))} left</span></span>
              <I n="arrow" s={28} />
              <span><span className="w-small">To</span><span className="w-mono w-strong big">{toBin}</span><span className="w-muted">now {fmt(sameAs(result.to.contents))}</span></span>
            </div>
            {result.moves.filter((m) => m.status === "failed").map((m) => <Alert key={m.id} title={`${m.itemNo} didn't move`}>{m.error}</Alert>)}
            {err && <Alert title="Couldn't undo">{err}</Alert>}
            {!undone && <div className="w-status ok"><span className="w-dot" />Posted in Business Central</div>}
          </div>
          <div className="w-foot">
            {contents.length > 0 && <button type="button" className="w-btn primary" onClick={moreFromSame}>Move more from {from}</button>}
            <div className="w-two">
              <button type="button" className="w-btn" onClick={reset}>New move</button>
              {!undone ? <button type="button" className="w-btn" onClick={undo} disabled={busy}>Undo</button> : <Link to="/wms" className="w-btn">Done</Link>}
            </div>
          </div>
        </>
      )}
    </>
  );
}
